import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../../../../api/queries/apiGet';
import { API_BASE_URL } from '../constants/consts';
import { authFirewallQueryKeys } from '../constants/queryKeys';
import { EntityDetails, EntityDetailsFull } from '../../types';
import { buildUniversalMfaDomainQueryParams } from '../constants/utils';
import { ActiveUsersAndResourcesMode, QueryFiltersDomainOnly } from '../types';

const END_POINT = '/metric/blockResource';
const PREVIEW_MODE_LIMIT = 9;
const FULL_MODE_LIMIT = 50;

type BlockedResourceDto = EntityDetails;
type BlockedResourceFullDto = Omit<EntityDetailsFull, 'lastAuthTime'> & { lastAuthTime: string };

const convertToBlockedResources = (data: BlockedResourceDto[]): EntityDetails[] => {
    return data.map((item) => ({
        name: item.name,
        hits: item.hits,
        guid: item.guid
    }));
};

const convertToBlockedResourcesFull = (data: BlockedResourceFullDto[]): EntityDetailsFull[] => {
    return data.map((item) => ({
        name: item.name,
        hits: item.hits,
        guid: item.guid,
        lastAuthTime: new Date(item.lastAuthTime),
        riskLevel: item.riskLevel,
        domain: item.domain
    }));
};

const fetchBlockedResources = async (
    filters: QueryFiltersDomainOnly,
    mode: ActiveUsersAndResourcesMode
): Promise<BlockedResourceDto[] | BlockedResourceFullDto[]> => {
    const data = await apiGet<BlockedResourceDto[] | BlockedResourceFullDto[]>(`${API_BASE_URL}${END_POINT}`, {
        ...buildUniversalMfaDomainQueryParams(filters),
        limit: mode === 'preview' ? PREVIEW_MODE_LIMIT.toString() : FULL_MODE_LIMIT.toString(),
        mode
    });
    return data;
};

export const useBlockedResourcesPreview = (filters: QueryFiltersDomainOnly): UseQueryResult<EntityDetails[]> => {
    return useQuery({
        queryKey: authFirewallQueryKeys.blockedResources(filters, 'preview'),
        queryFn: () => fetchBlockedResources(filters, 'preview') as Promise<BlockedResourceDto[]>,
        select: convertToBlockedResources
    });
};

export const useBlockedResourcesFull = (
    filters: QueryFiltersDomainOnly,
    enabled: boolean
): UseQueryResult<EntityDetailsFull[]> => {
    return useQuery({
        queryKey: authFirewallQueryKeys.blockedResources(filters, 'full'),
        queryFn: () => fetchBlockedResources(filters, 'full') as Promise<BlockedResourceFullDto[]>,
        select: convertToBlockedResourcesFull,
        enabled
    });
};
